import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { SelectListControlItem, ValidationModel, ValidationType } from '@app/shared/models';
import { Observable, catchError, map, of } from 'rxjs';
import { ConstrainedField } from '@shared/models/constrained-field';
import { SelectlistCellComponent } from '../selectlist-cell/selectlist-cell.component';
import { HttpParams } from '@angular/common/http';
import { createConstrainedModel } from '../../dynamic-editor-base';
import { FormGroup } from '@angular/forms';

@Component({
  selector: 'app-selectlist-control-cell',
  templateUrl: '../selectlist-cell/selectlist-cell.component.html',
  styleUrls: ['../selectlist-cell/selectlist-cell.component.scss']
})
export class SelectlistControlCellComponent extends SelectlistCellComponent implements OnInit, OnChanges, OnDestroy {
  @Output() constrainedFieldsChange: EventEmitter<Array<ConstrainedField>> = new EventEmitter<Array<ConstrainedField>>();
  @Output() formGroupChange: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
  @Input() public constrainedFields!: Array<ConstrainedField>;
  @Input() public formGroup!: FormGroup;
  @Input() public extraData!: SelectListControlExtraData;

  declare public selectItems$: Observable<SelectListControlItem[]>;

  private previousFControls: Partial<ValidationModel>[] = [];

  override ngOnInit() {
    super.ngOnInit();
    this.field.control.valueChanges.subscribe({
      next: (val: SelectListControlItem) => {
        if (!val) {
          return;
        }
        let newFControls = val.fControls?.filter(f => f.field != null).map(f => f.field);
        let prevValue = this.formGroup.getRawValue();

        this.destroyPreviousFcControls(newFControls, prevValue);

        this.previousFControls = [];
        let newConstrainedFields = [];

        val.fControls?.forEach(fControl => {
          let vm = this.extraData.validationModels.find(vm => vm.field == fControl.field);
          if (vm) {
            vm = { ...vm, ...fControl };
            let cf = this.constrainedFields.find(cf => cf.key == fControl.field);
            let createNewCf = (): ConstrainedField => {
              let newCf = createConstrainedModel([vm], prevValue)[0];
              if (newCf.controlType == "ddlControl") {
                let newValidationModels = [...this.extraData.validationModels];
                (newCf.extraData as SelectListControlExtraData).validationModels = newValidationModels;
              }
              return newCf;
            }
            if (!cf) {
              cf = createNewCf();
            }
            else {
              let oldExtraData = { ...cf.extraData };   
              let idx = this.constrainedFields.findIndex(cf => cf.key == fControl.field);
              this.constrainedFields.splice(idx, 1);
              cf = createNewCf();
              if ((["ddl", "ddlMultiple", "ddlSearch", "search", "checkboxList"] as ValidationType[]).includes(cf.controlType)) {
                cf.extraData = { ...oldExtraData, ...cf.extraData  }
              }
              if ((["ddlControl"] as ValidationType[]).includes(cf.controlType)) {
                cf.extraData.params = { ...oldExtraData.params, ...cf.extraData.params  }
              }
            }
            this.formGroup.setControl(fControl.field, cf.control, { emitEvent: false });
            newConstrainedFields.push(cf);
          }
        });

        let idxAfterCurrent = this.constrainedFields.findIndex(cf => cf.key == this.field.key) + 1;
        this.constrainedFields = [
          ...this.constrainedFields.slice(0, idxAfterCurrent),
          ...newConstrainedFields,
          ...this.constrainedFields.slice(idxAfterCurrent),
        ]
        this.constrainedFieldsChange.emit(this.constrainedFields);

        this.previousFControls = [...val.fControls ?? []];
      }
    })
  }

  override ngOnChanges(changes: SimpleChanges) {
    if (changes.extraData && !changes.extraData.firstChange) {
      this.setSelectItems();
    }
  }

  public override toDbModel(value: SelectListControlItem | SelectListControlItem[]): number | string | (string | number)[] {
    if (this.field.controlType === "ddlControl" && !Array.isArray(value)) {
      return value?.id ?? null
    };

    return null;
  }

  public override setSelectItems() {
    if (this.field.control.enabled) {
      this.field.control.disable({ emitEvent: false });
    }

    this.selectItems$ = this.service
      .getRefSelectListControl(`selectlist/control/${this.field.key}`, this.extraData.params)
      .pipe(
        catchError((err) => {
          this.snackBar.showButtonSnackbar(err);
          if (this.field.editable) {
            this.field.control.enable({ emitEvent: false });
          }
          return of({ selectItems: [] as SelectListControlItem[], defaultItem: null });
        }),
        map(({ selectItems, defaultItem }) => {
          if (this.field.editable) {
            this.field.control.enable({ emitEvent: false });
          }

          if (this.field.control.value) {
            if (typeof this.field.control.value === "string" || typeof this.field.control.value === "number") {
              this.field.control.setValue(selectItems.find(val => val.id === this.field.control.value))
            }

            if (typeof this.field.control.value === "object" && this.field.control.value.id) {
              this.field.control.setValue(selectItems.find(val => val.id === this.field.control.value.id))
            }
          }
          else {
            if (defaultItem) {
              this.field.control.setValue(defaultItem);
            }
          }

          return selectItems;
        })
      );
  }

  public override clearInput() {
    this.field.control.setValue(null);
  }

  public override canShowClearBtn(): boolean {
    if (this.field.editable == false) {
      return false;
    }
    return !!this.field.control.value && !!this.field.control.value?.id;
  }

  private destroyPreviousFcControls(newFControls: string[] = [], prevValue: object = {}) {
    if (this.previousFControls.length != 0) {
      this.previousFControls.forEach(fControl => {
        if (newFControls.includes(fControl.field)) {
          let control = this.formGroup.get(fControl.field);
          control.value ? prevValue[fControl.field] = control.value : null;
        }
        let idx = this.constrainedFields.findIndex(cf => cf.key == fControl.field);
        this.constrainedFields.splice(idx, 1);
        let vm = this.extraData.validationModels.find(vm => vm.field == fControl.field);
        if (vm) {
          let cf = createConstrainedModel([vm])[0];
          this.formGroup.setControl(fControl.field, cf.control, { emitEvent: false });
        }
      })
      this.constrainedFieldsChange.emit(this.constrainedFields);
    }
  }

  ngOnDestroy() {
    this.destroyPreviousFcControls();
  }
}


export interface SelectListControlExtraData {
  params: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> };
  validationModels: ValidationModel[];
}
